import { TFunction } from "next-i18next";
import {
    getMarketOverreactionReactionBarConfig,
    getSurprisedReactionBarConfig,
    getUnderlyingPricePredictionReactionBarConfig,
} from "src/components/user-reactions/config";
import { IRevealedReaction, IUserReactionBarConfig, IUserReactionWithBarConfig, TUserReactions } from "src/components/user-reactions/types";
import { ARTICLE_REACTIONS_SHOWN_KEY } from "src/consts/local-storage";
import { EnumReactionType } from "src/types/common-enum";
import { localStorage } from "src/utils/storage";

export const getConfigForReactionType = (type: EnumReactionType, t: TFunction): IUserReactionBarConfig | null => {
    switch (type) {
        case EnumReactionType.ArticleUserSurprise:
            return getSurprisedReactionBarConfig(t);
        case EnumReactionType.ArticleUserMarketOverReaction:
            return getMarketOverreactionReactionBarConfig(t);
        case EnumReactionType.ArticleUserPricePredictionUnderlying:
            return getUnderlyingPricePredictionReactionBarConfig(t);
        default:
            return null;
    }
};

export const getReactionsWithBarConfig = (reactions: TUserReactions, t: TFunction): ReadonlyArray<IUserReactionWithBarConfig> =>
    reactions.map(reaction => {
        const config = getConfigForReactionType(reaction.reactionType, t);

        return {
            ...reaction,
            label: config?.label ?? "",
            reactionValues: reaction.reactionValues.map(value => {
                const configValue = config?.options?.find(val => val.value === value.value);
                return {
                    ...value,
                    backgroundColor: configValue?.backgroundColor ?? "",
                    text: configValue?.text ?? "",
                };
            }),
        };
    });

export const getHighestReactionNumber = (reactions: TUserReactions): number => {
    return reactions.reduce((accumulator, reactionBar) => {
        const highestValue = reactionBar.reactionValues.reduce((acc, val) => (acc = val.numberOfReactions > acc ? val.numberOfReactions : acc), 0);
        return highestValue > accumulator ? highestValue : accumulator;
    }, 0);
};

export const hasUserRevealedReactions = (slug: string) => {
    const storedReactions = localStorage.get<IRevealedReaction[]>(ARTICLE_REACTIONS_SHOWN_KEY) ?? [];
    return storedReactions.some(reaction => reaction.slug === slug);
};

export const setUserRevealedReactions = (slug?: string) => {
    if (!slug) {
        return;
    }

    const storedReactions = localStorage.get<IRevealedReaction[]>(ARTICLE_REACTIONS_SHOWN_KEY) ?? [];
    if (storedReactions.length > 0) {
        const dayInMs = 1000 * 60 * 60 * 24;
        const date = Date.now();
        for (const reaction of storedReactions) {
            if (reaction.date < date - dayInMs) {
                storedReactions.splice(storedReactions.indexOf(reaction), 1);
            }
        }
    }

    storedReactions.push({ slug, date: Date.now() });
    localStorage.set(ARTICLE_REACTIONS_SHOWN_KEY, storedReactions);
};
